import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * FOLDER API CLIENT
 *
 * Axios instance configured for folder-related API calls.
 * Uses same JWT authentication pattern as other API files.
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all outgoing requests.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * RESPONSE INTERCEPTOR
 * Handles global error responses, especially authentication failures.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      console.error('Unauthorized access - clearing token cache')
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// FOLDER API FUNCTIONS
// ============================================================================

/**
 * Fetch folder tree for a release (hierarchical structure)
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Array>} Hierarchical array of folder objects with children
 */
export const fetchFolderTree = async (releaseId) => {
  try {
    const response = await apiClient.get(`/folders/release/${releaseId}/tree`)
    return response.data
  } catch (error) {
    console.error('Error fetching folder tree:', error)
    throw error
  }
}

/**
 * Fetch flat list of folders for a release
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Array>} Flat array of folder objects
 */
export const fetchFolders = async (releaseId) => {
  try {
    const response = await apiClient.get(`/folders/release/${releaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching folders:', error)
    throw error
  }
}

/**
 * Fetch a single folder by ID
 *
 * @param {number|string} folderId - Folder ID
 * @returns {Promise<Object>} Folder object
 */
export const fetchFolderById = async (folderId) => {
  try {
    const response = await apiClient.get(`/folders/${folderId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching folder:', error)
    throw error
  }
}

/**
 * Create a new folder
 *
 * @param {Object} folderData - Folder data
 * @param {string} folderData.name - Folder name
 * @param {number|string|null} folderData.parentId - Parent folder ID (null for root)
 * @param {number|string} folderData.releaseId - Release ID
 * @param {number} folderData.orderIndex - Order index for sorting
 * @returns {Promise<Object>} Created folder object
 */
export const createFolder = async (folderData) => {
  try {
    const response = await apiClient.post('/folders', folderData)
    return response.data
  } catch (error) {
    console.error('Error creating folder:', error)
    throw error
  }
}

/**
 * Update an existing folder
 *
 * @param {number|string} folderId - Folder ID
 * @param {Object} updates - Fields to update { name?, orderIndex? }
 * @returns {Promise<Object>} Updated folder object
 */
export const updateFolder = async (folderId, updates) => {
  try {
    const response = await apiClient.put(`/folders/${folderId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating folder:', error)
    throw error
  }
}

/**
 * Move a folder to a new parent or reorder
 *
 * @param {number|string} folderId - Folder ID
 * @param {Object} moveData - Move data { newParentId, orderIndex }
 * @returns {Promise<Object>} Updated folder object
 */
export const moveFolder = async (folderId, moveData) => {
  try {
    const response = await apiClient.put(`/folders/${folderId}/move`, moveData)
    return response.data
  } catch (error) {
    console.error('Error moving folder:', error)
    throw error
  }
}

/**
 * Delete a folder
 * Note: This cascade deletes all scenarios within the folder
 *
 * @param {number|string} folderId - Folder ID
 * @returns {Promise<void>}
 */
export const deleteFolder = async (folderId) => {
  try {
    const response = await apiClient.delete(`/folders/${folderId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting folder:', error)
    throw error
  }
}

/**
 * Fetch scenarios in a folder
 *
 * @param {number|string} folderId - Folder ID
 * @returns {Promise<Array>} Array of test scenario objects
 */
export const fetchScenariosInFolder = async (folderId) => {
  try {
    const response = await apiClient.get(`/folders/${folderId}/testscenarios`)
    return response.data
  } catch (error) {
    console.error('Error fetching scenarios in folder:', error)
    throw error
  }
}

/**
 * Fetch root scenarios (not in any folder) for a release
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Array>} Array of test scenario objects
 */
export const fetchRootScenarios = async (releaseId) => {
  try {
    const response = await apiClient.get(`/releases/${releaseId}/testscenarios/root`)
    return response.data
  } catch (error) {
    console.error('Error fetching root scenarios:', error)
    throw error
  }
}

/**
 * Fetch all scenarios for a release (across all folders)
 *
 * @param {number|string} releaseId - Release ID
 * @returns {Promise<Array>} Array of test scenario objects
 */
export const fetchAllScenariosInRelease = async (releaseId) => {
  try {
    const response = await apiClient.get(`/releases/${releaseId}/testscenarios`)
    return response.data
  } catch (error) {
    console.error('Error fetching all scenarios:', error)
    throw error
  }
}

/**
 * Move a scenario to a different folder
 *
 * @param {number|string} testScenarioId - Test scenario ID
 * @param {Object} moveData - Move data { folderId, orderIndex }
 * @returns {Promise<Object>} Updated test scenario object
 */
export const moveScenarioToFolder = async (testScenarioId, moveData) => {
  try {
    const response = await apiClient.put(
      `/folders/testscenarios/${testScenarioId}/move`,
      moveData
    )
    return response.data
  } catch (error) {
    console.error('Error moving scenario:', error)
    throw error
  }
}

// ============================================================================
// TEST SCENARIO API FUNCTIONS (for folder browser)
// ============================================================================

/**
 * Create a new test scenario
 *
 * @param {Object} scenarioData - Scenario data
 * @param {string} scenarioData.scenarioName - Scenario name
 * @param {string} scenarioData.description - Description (optional)
 * @param {string} scenarioData.priority - Priority (Low, Medium, High, Critical)
 * @param {number|string} scenarioData.releaseId - Release ID
 * @param {number|string|null} scenarioData.folderId - Folder ID (null for root)
 * @param {number} scenarioData.orderIndex - Order index for sorting
 * @returns {Promise<Object>} Created test scenario object
 */
export const createTestScenario = async (scenarioData) => {
  try {
    const response = await apiClient.post('/testscenarios', scenarioData)
    return response.data
  } catch (error) {
    console.error('Error creating test scenario:', error)
    throw error
  }
}

/**
 * Update a test scenario
 *
 * @param {number|string} testScenarioId - Test scenario ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated test scenario object
 */
export const updateTestScenarioInFolder = async (testScenarioId, updates) => {
  try {
    const response = await apiClient.put(`/testscenarios/${testScenarioId}`, updates)
    return response.data
  } catch (error) {
    console.error('Error updating test scenario:', error)
    throw error
  }
}

/**
 * Delete a test scenario
 *
 * @param {number|string} testScenarioId - Test scenario ID
 * @returns {Promise<void>}
 */
export const deleteTestScenario = async (testScenarioId) => {
  try {
    const response = await apiClient.delete(`/testscenarios/${testScenarioId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting test scenario:', error)
    throw error
  }
}

/**
 * Reorder scenarios within a folder
 * Updates the orderIndex for each scenario in the list
 *
 * @param {Array} reorderData - Array of { id, orderIndex } objects
 * @returns {Promise<Array>} Updated scenarios
 */
export const reorderScenarios = async (reorderData) => {
  try {
    // Batch update - update each scenario's orderIndex
    const updatePromises = reorderData.map(({ id, orderIndex }) =>
      apiClient.put(`/testscenarios/${id}`, { orderIndex })
    )
    const responses = await Promise.all(updatePromises)
    return responses.map(r => r.data)
  } catch (error) {
    console.error('Error reordering scenarios:', error)
    throw error
  }
}

/**
 * Reorder folders within a parent
 * Sends only the dragged folder's new orderIndex - backend adjusts other folders
 *
 * @param {Array} reorderData - Array with single { id, orderIndex } object for the dragged folder
 * @returns {Promise<Array>} Updated folder data
 */
export const reorderFolders = async (reorderData) => {
  try {
    // Only send the dragged folder's new orderIndex - backend handles the rest
    const { id, orderIndex } = reorderData[0]
    const response = await apiClient.put(`/folders/${id}`, { orderIndex })
    return [response.data]
  } catch (error) {
    console.error('Error reordering folders:', error)
    throw error
  }
}

// ============================================================================
// CLONE API FUNCTIONS
// ============================================================================

/**
 * Clone a folder with all contents
 * Creates a deep copy of the folder including all sub-folders and scenarios.
 *
 * @param {number|string} folderId - Folder ID to clone
 * @returns {Promise<Object>} Cloned folder object
 */
export const cloneFolder = async (folderId, targetReleaseId) => {
  try {
    const response = await apiClient.post(`/folders/${folderId}/clone`, { targetReleaseId })
    return response.data
  } catch (error) {
    console.error('Error cloning folder:', error)
    throw error
  }
}

/**
 * Clone a test scenario with all test cases and steps
 * Creates a deep copy of the scenario including all test cases, steps, and runs.
 *
 * @param {number|string} testScenarioId - Test scenario ID to clone
 * @returns {Promise<Object>} Cloned scenario object
 */
export const cloneTestScenario = async (testScenarioId) => {
  try {
    const response = await apiClient.post(`/testscenarios/${testScenarioId}/clone`)
    return response.data
  } catch (error) {
    console.error('Error cloning test scenario:', error)
    throw error
  }
}

/**
 * Bulk clone multiple test scenarios
 * Clones multiple scenarios at once, optionally into a target folder.
 *
 * @param {Object} bulkCloneData - Bulk clone data
 * @param {number[]} bulkCloneData.scenarioIds - Array of scenario IDs to clone
 * @param {number|null} bulkCloneData.targetFolderId - Target folder ID (null to keep in source folder)
 * @returns {Promise<Array>} Array of cloned scenario objects
 */
export const bulkCloneScenarios = async (bulkCloneData) => {
  try {
    const response = await apiClient.post('/testscenarios/bulk-clone', bulkCloneData)
    return response.data
  } catch (error) {
    console.error('Error bulk cloning scenarios:', error)
    throw error
  }
}

/**
 * Clone a folder to a specific target parent folder
 * Creates a deep copy of the folder including all sub-folders, scenarios, test cases, and steps.
 * Optionally places the clone under a different parent folder.
 *
 * @param {number|string} folderId - Folder ID to clone
 * @param {Object} cloneData - Clone target data
 * @param {number|null} cloneData.targetParentFolderId - Target parent folder ID (null for same parent)
 * @returns {Promise<Object>} Cloned folder object
 */
export const cloneFolderToTarget = async (folderId, cloneData = {}) => {
  try {
    const response = await apiClient.post(`/folders/${folderId}/clone`, cloneData)
    return response.data
  } catch (error) {
    console.error('Error cloning folder to target:', error)
    throw error
  }
}
